/* Auth session storage.
 *
 * We use localStorage with a stamped expiry rather than a cookie because:
 *   1. The backend is a separate origin in dev (proxied) and prod (likely
 *      different host). Cookies would need explicit CORS + SameSite plumbing.
 *   2. Tokens here are short-lived (24h) magic-link sessions; the UX cost of
 *      re-login is low and the threat model assumes a trusted personal device.
 */

import type { AuthSession } from './types';

const STORAGE_KEY = 'networker.session.v1';

export function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed.token || !parsed.expires_at) return null;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) {
      // Expired — clean up so callers aren't tempted to send a dead token.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: AuthSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function isAuthenticated(): boolean {
  return loadSession() !== null;
}
